// ============================================================
// statementBits.tsx — (batch 2) small pieces shared by the settlement
// statement panel's parts (StatementPanel, statement/*): the status chip, a
// figure tile, the maker → checker step, the pencil, focusing a field by id,
// and the "{ saved: true, reload: true }" reply check.
// ============================================================
import React from 'react'
import { BIcon, B_ICON, money } from '../../billing/billingUi'
import { STATUS_LABEL, type CaseStatements, type Capacity, type StatementSummary } from '../../../types/settlementStatement.types'

export const CHIP = 'inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.06em] px-2 py-[2px] rounded-[5px] border whitespace-nowrap'
const CHIP_TONE: Record<string, string> = {
  DRAFT: 'border-casevo-border bg-casevo-input text-casevo-text-soft',
  ISSUED: 'border-casevo-brand/40 bg-casevo-mint text-casevo-brand dark:text-casevo-link',
  APPROVED: 'border-casevo-brand bg-casevo-brand text-white',
  SUPERSEDED: 'border-casevo-warn-border bg-casevo-warn-bg text-casevo-warn',
}
export const StatusChip: React.FC<{ status: StatementSummary['status'] }> = ({ status }) => <span className={`${CHIP} ${CHIP_TONE[status]}`}>{STATUS_LABEL[status]}</span>
export const stRef = (s: Pick<StatementSummary, 'number' | 'version'>): string => `${s.number ?? 'Draft'} v${s.version}`
export const shortHash = (h: string | null): string => (h ? `${h.slice(0, 8)}…` : '—')

// (E-sign fixes B) A row of figure tiles that wraps by the room it has (not a
// fixed 3 columns): each tile grows from ~8.5rem, so at 360 px / 200% zoom a
// tile that doesn't fit drops to its own full-width row — no clipped £ amount,
// no sideways scroll.
export const FIGURE_GRID = 'flex flex-wrap gap-[10px] [&>*]:flex-[1_1_8.5rem] [&>*]:min-w-0'
// A figure tile; `sub` sits under the amount (e.g. where the net sum goes).
// Long labels / amounts wrap inside the tile rather than overflow it.
export const Figure: React.FC<{ label: string; pence: number; strong?: boolean; sub?: React.ReactNode }> = ({ label, pence, strong, sub }) => (
  <div className={`min-w-0 rounded-[10px] border px-[12px] py-[8px] ${strong ? 'border-casevo-brand/40 bg-casevo-mint' : 'border-casevo-border bg-casevo-input'}`} data-figure={label}>
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-casevo-text-soft break-words">{label}</div>
    <div className={`tabular-nums [overflow-wrap:anywhere] ${strong ? 'text-[17px] font-bold' : 'text-[15px] font-semibold'} text-casevo-forest`}>{money(pence)}</div>
    {sub && <div className="text-[12px] leading-[1.35] text-casevo-text-soft break-words">{sub}</div>}
  </div>
)

export const Step: React.FC<{ done: boolean; label: string; who?: string }> = ({ done, label, who }) => (
  <span className={`inline-flex items-center gap-[6px] text-[13px] ${done ? 'text-casevo-brand dark:text-casevo-link font-semibold' : 'text-casevo-text-soft'}`}>
    <span className={`w-[18px] h-[18px] rounded-full grid place-items-center border ${done ? 'bg-casevo-brand border-casevo-brand text-white' : 'border-casevo-border'}`}>{done && <BIcon name="check" size={11} strokeWidth={3} />}</span>
    {label}{who ? <span className="font-normal text-casevo-text-soft"> · {who}</span> : null}
  </span>
)

// The read-first pencil: a summary's one way into its editor.
export const EditPencil: React.FC<{ label: string; onClick: () => void; disabled?: boolean }> = ({ label, onClick, disabled }) => (
  <button type="button" className={B_ICON} aria-label={label} title={label} disabled={disabled} onClick={onClick}><BIcon name="pen" size={14} /></button>
)

// A read-only "label: value" row of a summary.
export const SummaryRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[12px] font-semibold text-casevo-text-soft">{label}</dt>
    <dd className="m-0 text-[13.5px] text-casevo-forest break-words">{children}</dd>
  </div>
)

// Focus the field with this id (or the first input / select inside it — a
// DateInput's day box), and bring it into view. False when it isn't there.
export const focusById = (id: string): boolean => {
  const el = document.getElementById(id)
  if (!el) return false
  const target = el.matches('input, select, textarea, button') ? el : el.querySelector<HTMLElement>('input, select, textarea')
  if (!target) return false
  target.focus()
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

// (Batch 2) A write may answer `{ saved: true, reload: true }` (saved, but the
// fresh list couldn't be built) — the panel re-reads.
export const isReloadReply = (r: unknown): boolean =>
  !!r && typeof r === 'object' && (r as { reload?: unknown }).reload === true && !('statements' in (r as object))

// A child / protected party? From the draft, else the case's latest
// statement, else a recorded litigation friend; null = not known (a new
// statement's payee is then left to the server).
export const knownChildOrPp = (capacity: Capacity | null | undefined, data: CaseStatements): boolean | null => {
  const c = capacity ?? data.statements[0]?.capacity ?? null
  if (c) return c !== 'ADULT'
  return data.litigationFriendName ? true : null
}
